/**
 * Internal query modifier that excludes products tagged `hide-search` from the
 * full search results page. It must reach the search request but must never be
 * written into a visible input, or the browser will show it, remember it in the
 * field's autofill history, and restore it on back/forward navigation.
 *
 * It is deliberately placed *before* the customer's term: storefront search
 * applies partial word matching to the last term only, so a trailing modifier
 * would consume the match the customer needs.
 */
const HIDDEN_PRODUCT_FILTER = '-tag:hide-search';

/**
 * Resource types returned by the full search results page. Storefront search
 * defaults to every type, which pulls CMS pages into the product grid — keep
 * this in step with the hidden `type` inputs in the search and facet forms.
 */
const SEARCH_RESULT_TYPES = 'product,article';

/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
		this.form = this.container.querySelector('form.searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.defaultTab = this.container.querySelector('.side-panel-content--initial');
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    document.querySelectorAll('form[role="search"]').forEach((form) => {
      if (form === this.form) {
        return;
      }

      form.addEventListener('submit', (event) => {
        event.preventDefault();

        const input = form.querySelector('input[name="q"]');
        if (!input) {
          return;
        }

        const searchTerm = this.stripHiddenProductFilter(input.value);
        if (!searchTerm.length) {
          return;
        }

        window.location.assign(this.buildSearchUrl(searchTerm));
      });
    });

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.button.forEach((item, i) => {
    	item.addEventListener('click', (event) => {
				var _this = this;
				event.preventDefault();
				document.getElementsByTagName("body")[0].classList.toggle('open-cc');
	      this.container.classList.toggle('active');
				if (this.container.classList.contains('active')) {
					setTimeout(function() {
						_this.input.focus({ preventScroll:true });
					}, 100);
				}

				return false;
	    });
    });
  }

  getQuery() {
    return this.stripHiddenProductFilter(this.input.value);
  }

  stripHiddenProductFilter(value) {
    return value.split(HIDDEN_PRODUCT_FILTER).join(' ').replace(/\s+/g, ' ').trim();
  }

  buildSearchUrl(searchTerm) {
    const params = new URLSearchParams();

    // The modifier goes first so `options[prefix]=last` applies prefix matching
    // to the customer's own term rather than to the modifier.
    params.set('q', `${HIDDEN_PRODUCT_FILTER} ${searchTerm}`);
    params.set('type', SEARCH_RESULT_TYPES);
    params.set('options[prefix]', 'last');

    return `${theme.routes.search_url}?${params.toString()}`;
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('active');
      }
      return;
    }
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.add('active');
      this.getSearchResults(searchTerm);
    }
  }

  onFormSubmit(event) {
    event.preventDefault();

    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      return;
    }

    window.location.assign(this.buildSearchUrl(searchTerm));
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.add('loading');
    }

    // The Predictive Search API does not support query syntax such as
    // `-tag:hide-search` — it would be matched as literal search text and wreck
    // the suggestions. Products tagged `hide-search` are excluded from the
    // dropdown at render time instead, in sections/predictive-search.liquid.
    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`)
      .then((response) => {
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.classList.remove('loading');
        }
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }

		let _this = this,
				submitButton = this.container.querySelector('#search-results-submit');

		if (!submitButton) {
			return;
		}

		submitButton.addEventListener('click', () => {
			const searchTerm = _this.getQuery();
			if (!searchTerm.length) {
				return;
			}

			window.location.assign(_this.buildSearchUrl(searchTerm));
		} );
  }

  close() {
    this.container.classList.remove('active');
  }
}
function initPredictiveSearch() {
  if (typeof PredictiveSearch !== 'undefined' && !window.__predictiveSearchInitialized) {
    if (document.getElementById('Search-Drawer')) {
      window.__predictiveSearchInitialized = true;
      new PredictiveSearch();
    }
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPredictiveSearch);
} else {
  initPredictiveSearch();
}
window.addEventListener('load', initPredictiveSearch);
